import moment from "moment";
import { useNavigate } from "react-router";

import {
  DeleteGLDAdditionTask,
  EditGMNMeasuringPointTask,
  EditGMWEventTask,
  GLDClosureTask,
  qualityRegimeOptionsType,
} from "@nens/brostar/uploadapi-client";
import { ColumnDefinition } from "@nens/chakra/components";

import { getApi } from "@/api/api-configuration";
import { convertGLDAdditionToEditTask } from "@/helpers/convert-gld-to-task";
import { convertGMNMeasuringPointToEditTask } from "@/helpers/convert-gmn-measuring-point-to-task";
import { convertGMWEventToEditTask } from "@/helpers/convert-gmw-event-to-task";
import { isValidEnum } from "@/helpers/is-valid-enum";
import { useInvalidateGldQueries } from "@/queries/query-gld";
import { useInvalidateGldObservationQueries } from "@/queries/query-gld-observation";
import {
  useGmnMeasuringPointSingleQueryDirectly,
  useInvalidateGmnMeasuringPointQueries,
} from "@/queries/query-gmn-measuring-point";
import { useInvalidateGmwEventQueries } from "@/queries/query-gmw-event";

import { DeleteButton } from "./components/delete-button-self";

type DeleteButtonProps = {
  uuid: string;
};

const viewType = new URLSearchParams(window.location.search).get("viewType");

const createDeleteTask = <
  T extends EditGMWEventTask | EditGMNMeasuringPointTask | DeleteGLDAdditionTask
>(
  editTask: T,
  projectNumber: string,
  correctionReason: string
) => ({
  ...editTask,
  metadata: { ...editTask.metadata, projectNumber, correctionReason },
  sourcedocumentData: {
    ...editTask.sourcedocumentData,
    ...("eventDate" in editTask.sourcedocumentData
      ? { eventDate: moment(editTask.sourcedocumentData.eventDate).format("YYYY-MM-DD") }
      : {}),
  },
});
export const DeleteGmwEventButton = ({ uuid }: DeleteButtonProps) => {
  const api = getApi();
  const navigate = useNavigate();
  const invalidateGmwEventList = useInvalidateGmwEventQueries();
  return (
    <DeleteButton
      onDelete={async (projectNumber, correctionReason) => {
        const event = await api.apiGmwEventsRead({ uuid });
        const gmw = await api.apiGmwGmwsRead({ uuid: event.gmw });
        const editTask = convertGMWEventToEditTask(event, gmw);
        const deleteTask = createDeleteTask(editTask, projectNumber, correctionReason);
        await api.deleteGMWEvent(deleteTask);

        invalidateGmwEventList(gmw.broId);
        if (viewType !== "gmwTable") navigate("/?viewType=gmwTable");
      }}
      type="GMW gebeurtenis"
    />
  );
};

export const DeleteGmnMeausuringPointButton = ({ uuid }: DeleteButtonProps) => {
  const api = getApi();
  const navigate = useNavigate();
  const invalidateGmnMeasuringPointList = useInvalidateGmnMeasuringPointQueries();
  const gmnMeasuringPointSingleQueryDirectly = useGmnMeasuringPointSingleQueryDirectly();
  return (
    <DeleteButton
      onDelete={async (projectNumber, correctionReason) => {
        const measuringPoint = await gmnMeasuringPointSingleQueryDirectly(uuid);
        const gmn = await api.apiGmnGmnsRead({ uuid: measuringPoint.gmn });
        const editTask = convertGMNMeasuringPointToEditTask(gmn, measuringPoint);
        const deleteTask = createDeleteTask(editTask, projectNumber, correctionReason);
        await api.deleteGmnMeasuringPoint(deleteTask);

        invalidateGmnMeasuringPointList(measuringPoint.gmn);
        if (viewType !== "gmnTable") navigate("/?viewType=gmnTable");
      }}
      type="GMN meetpunt"
    />
  );
};

export const DeleteGldObservationButton = ({ uuid }: DeleteButtonProps) => {
  const api = getApi();
  const navigate = useNavigate();
  const invalidateGldObservationList = useInvalidateGldObservationQueries();
  return (
    <DeleteButton
      onDelete={async (projectNumber, correctionReason) => {
        const observation = await api.apiGldObservationsRead({ uuid });
        const gld = await api.apiGldGldsRead({ uuid: observation.gld });
        const editTask = convertGLDAdditionToEditTask(observation, gld);
        const deleteTask: DeleteGLDAdditionTask = {
          metadata: {
            ...editTask.metadata,
            projectNumber,
            correctionReason,
          },
          sourcedocumentData: {
            ...editTask.sourcedocumentData,
            beginPosition: observation.beginPosition.toISOString(),
            endPosition: observation.endPosition.toISOString(),
            timeValuePairs: [],
          },
        };
        await api.deleteGldAddition(deleteTask);

        invalidateGldObservationList(observation.gld);
        if (viewType !== "gldTable") navigate("/?viewType=gldTable");
      }}
      type="GLD observatie"
    />
  );
};

export const DeleteGldClosureButton = ({ uuid }: DeleteButtonProps) => {
  const api = getApi();
  const navigate = useNavigate();
  const invalidateGldList = useInvalidateGldQueries();
  return (
    <DeleteButton
      onDelete={async (projectNumber, correctionReason) => {
        const gld = await api.apiGldGldsRead({ uuid });
        const deleteTask: GLDClosureTask = {
          metadata: {
            broId: gld.broId,
            projectNumber,
            correctionReason,
            qualityRegime: isValidEnum(qualityRegimeOptionsType, gld.qualityRegime),
            deliveryAccountableParty: gld.deliveryAccountableParty ?? "",
          },
        };
        await api.deleteGldClosure(deleteTask);

        await invalidateGldList();
        if (viewType !== "gldTable") navigate("/?viewType=gldTable");
      }}
      type="GLD beeindiging"
    />
  );
};

// Small util function to add delete button to table column functions
export const addDeleteButton = <T extends { uuid?: string | undefined }>(
  columnDefinitions: ColumnDefinition<T>[],
  DeleteButton:
    | typeof DeleteGmwEventButton
    | typeof DeleteGmnMeausuringPointButton
    | typeof DeleteGldObservationButton
    | typeof DeleteGldClosureButton
): ColumnDefinition<T>[] => [
  ...columnDefinitions,
  {
    title: "",
    renderFunction: (row) => <DeleteButton uuid={row.uuid + ""} />,
    width: "50px",
  },
];
